import { useAiToolkitContext } from '@app/(main)/(dark)/ai/_shared/_context/AiToolkitContext';
import { type JSX, type ReactNode, useCallback } from 'react';

import { ConfirmationModal, Text } from '@videoblocks/design-system';

import { pluralize } from '../../../../../../../utils/utils';
import { getCreditCost } from '../../_context/getCreditCost';
import {
    AiToolkitCategory,
    AiToolkitOperation,
    AI_TOOLKIT_CATEGORIES,
    AI_TOOLKIT_OPERATIONS,
} from '../../_context/types';

type ConfirmCreditSpendModalProps = {
    onConfirm: (operation?: AiToolkitOperation) => void | Promise<void>;
};

const formatCredits = (credits: number): string => `${credits.toLocaleString()} ${pluralize(credits, 'credit')}`;

const getModalTitle = (category: AiToolkitCategory, calculatedCost: number, operation?: AiToolkitOperation): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return `Generate this video for ${formatCredits(calculatedCost)}?`;
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return `Generate this image for ${formatCredits(calculatedCost)}?`;
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            if (operation === AI_TOOLKIT_OPERATIONS.UPSCALE) {
                return 'Upscale your edit to 4K resolution';
            }
            return `Edit this video for ${formatCredits(calculatedCost)}?`;
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

// ConfirmationModal wraps `content` in a <p>, so these lines are spans made block-level.
const getUpscaleModalContent = (calculatedCost: number, remainingCredits: number): ReactNode => (
    <>
        Your edited video&apos;s resolution is currently HD - would you like to use your credits to upscale it to 4K
        (2160p)?
        <Text asChild fontWeight="bold" className="block mt-4">
            <span>Cost: {formatCredits(calculatedCost)}</span>
        </Text>
        <Text asChild fontWeight="regular" className="block mt-2">
            <span>Remaining: {formatCredits(remainingCredits)}</span>
        </Text>
    </>
);

const getModalContent = (
    category: AiToolkitCategory,
    calculatedCost: number,
    remainingCredits: number,
    operation?: AiToolkitOperation,
): ReactNode => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return `You'll have ${formatCredits(remainingCredits)} remaining.`;
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return `You'll have ${formatCredits(remainingCredits)} remaining.`;
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            if (operation === AI_TOOLKIT_OPERATIONS.UPSCALE) {
                return getUpscaleModalContent(calculatedCost, remainingCredits);
            }
            return `You'll have ${formatCredits(remainingCredits)} remaining.`;
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

const getModalConfirmLabel = (category: AiToolkitCategory, operation?: AiToolkitOperation): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return 'Generate Video';
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return 'Generate Image';
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            return operation === AI_TOOLKIT_OPERATIONS.UPSCALE ? 'Upscale to 4K' : 'Edit Video';
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

export const ConfirmCreditSpendModal = ({ onConfirm }: ConfirmCreditSpendModalProps): JSX.Element => {
    const { confirmationModal, dispatchConfirmationModal, credits, formStates, submitStates } = useAiToolkitContext();
    const { type, category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION } = confirmationModal ?? {};
    const isOpen = type === 'creditConfirmation' && confirmationModal?.isOpen === true;
    const operation = confirmationModal?.operation;
    const calculatedCost = getCreditCost(category, formStates, { operation, submitStates });
    const remainingCredits = credits - calculatedCost;

    const onCancel = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
    }, [dispatchConfirmationModal]);

    const handleConfirm = useCallback((): void => {
        void onConfirm(operation);
    }, [onConfirm, operation]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            title={getModalTitle(category, calculatedCost, operation)}
            content={getModalContent(category, calculatedCost, remainingCredits, operation)}
            confirmLabel={getModalConfirmLabel(category, operation)}
            cancelLabel="Cancel"
            onConfirm={handleConfirm}
            onCancel={onCancel}
        />
    );
};
